import {
  FaUsers,
  FaBullseye,
  FaHandshake,
  FaRoad,
  FaCamera,
  FaMapMarkerAlt,
  FaComments,
  FaCheckCircle,
} from "react-icons/fa";
import { Link } from "react-router-dom";

const About = () => {
  const values = [
    {
      icon: FaUsers,
      title: "Community first",
      text: "Citizens, neighbors, and local teams share one clear view of road hazards.",
      tone: "bg-navy-900/5 text-navy-800",
    },
    {
      icon: FaBullseye,
      title: "Transparent tracking",
      text: "Every report has a status — from reported to verified, in progress, or fixed.",
      tone: "bg-amber-500/15 text-amber-700",
    },
    {
      icon: FaHandshake,
      title: "Practical collaboration",
      text: "Votes and comments help surface the worst spots so repairs get prioritized.",
      tone: "bg-emerald-500/15 text-emerald-700",
    },
  ];

  const steps = [
    {
      icon: FaCamera,
      title: "Spot & report",
      text: "Add location, photo, severity, and a short note in minutes.",
    },
    {
      icon: FaMapMarkerAlt,
      title: "Map & prioritize",
      text: "Reports show up for the community so urgent hazards rise quickly.",
    },
    {
      icon: FaComments,
      title: "Discuss & vote",
      text: "Neighbors confirm impact and keep attention on the right places.",
    },
    {
      icon: FaCheckCircle,
      title: "Track the fix",
      text: "Follow status updates until the hazard is marked fixed.",
    },
  ];

  return (
    <div className="min-h-screen pb-16">
      <section className="relative overflow-hidden bg-gradient-to-br from-navy-950 via-navy-900 to-navy-700 px-4 py-14 text-white sm:py-20">
        <div className="pointer-events-none absolute inset-0 opacity-40 [background-image:radial-gradient(circle_at_20%_20%,rgba(251,191,36,0.28),transparent_40%),radial-gradient(circle_at_85%_10%,rgba(255,255,255,0.1),transparent_35%)]" />
        <div className="relative mx-auto max-w-4xl text-center">
          <p className="mb-3 text-sm font-semibold uppercase tracking-[0.2em] text-amber-400">
            About FixMyRoad
          </p>
          <h1 className="font-display text-4xl font-bold leading-tight sm:text-5xl md:text-6xl">
            Safer roads start with a shared report
          </h1>
          <p className="mx-auto mt-4 max-w-2xl text-base text-white/75 sm:text-lg">
            FixMyRoad helps communities document potholes, rally attention, and
            push for repairs with clear, public tracking.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 py-12 sm:py-16">
        <div className="grid items-center gap-8 lg:grid-cols-2 lg:gap-12">
          <div>
            <p className="mb-2 text-sm font-semibold uppercase tracking-[0.18em] text-amber-600">
              Our mission
            </p>
            <h2 className="font-display text-3xl font-bold text-navy-900 sm:text-4xl">
              Make hazards visible before they cause damage
            </h2>
            <p className="mt-4 text-navy-800/75">
              Roads fail quietly until someone notices. FixMyRoad turns that
              notice into a structured report — location, photo, severity — so
              communities and authorities can act with better information.
            </p>
            <p className="mt-3 text-navy-800/75">
              We focus on clarity over clutter: report fast, browse easily, and
              keep every hazard accountable from submission to fix.
            </p>
          </div>

          <div className="rounded-3xl border border-navy-900/10 bg-white p-7 shadow-[0_18px_50px_rgba(11,31,68,0.08)] sm:p-8">
            <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-2xl bg-amber-500 text-navy-950">
              <FaRoad className="text-3xl" />
            </div>
            <h3 className="text-center font-display text-2xl font-semibold text-navy-900">
              Built for real streets
            </h3>
            <p className="mt-3 text-center text-navy-800/70">
              Designed for Nepal neighborhoods and growing cities — mobile-first
              reporting when you&apos;re standing next to the hazard.
            </p>
          </div>
        </div>
      </section>

      <section className="bg-navy-900/5 px-4 py-12 sm:py-16">
        <div className="mx-auto max-w-6xl">
          <div className="mb-8 text-center sm:mb-10">
            <h2 className="font-display text-3xl font-bold text-navy-900">
              What we stand for
            </h2>
            <p className="mx-auto mt-2 max-w-2xl text-navy-800/70">
              Simple principles that keep the product useful for everyday
              citizens.
            </p>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 sm:gap-6">
            {values.map(({ icon: Icon, title, text, tone }) => (
              <div
                key={title}
                className="rounded-2xl border border-navy-900/8 bg-white p-6 shadow-sm transition hover:-translate-y-1"
              >
                <div
                  className={`mb-4 flex h-12 w-12 items-center justify-center rounded-xl ${tone}`}
                >
                  <Icon className="text-xl" />
                </div>
                <h3 className="font-display text-xl font-semibold text-navy-900">
                  {title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-navy-800/70">
                  {text}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 py-12 sm:py-16">
        <div className="mb-8 text-center sm:mb-10">
          <h2 className="font-display text-3xl font-bold text-navy-900">
            How it works
          </h2>
          <p className="mx-auto mt-2 max-w-2xl text-navy-800/70">
            Four steps from sidewalk to status update.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {steps.map(({ icon: Icon, title, text }, index) => (
            <div
              key={title}
              className="relative rounded-2xl border border-navy-900/10 bg-white p-5 shadow-sm"
            >
              <span className="absolute right-4 top-4 font-display text-3xl font-bold text-navy-900/10">
                {index + 1}
              </span>
              <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl bg-amber-500/15 text-amber-700">
                <Icon />
              </div>
              <h3 className="font-display text-lg font-semibold text-navy-900">
                {title}
              </h3>
              <p className="mt-2 text-sm text-navy-800/70">{text}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="px-4">
        <div className="mx-auto max-w-6xl overflow-hidden rounded-3xl bg-gradient-to-br from-navy-950 via-navy-900 to-navy-700 px-6 py-12 text-white sm:px-10 sm:py-14">
          <div className="grid gap-8 md:grid-cols-[1.2fr_0.8fr] md:items-center">
            <div>
              <h2 className="font-display text-3xl font-bold sm:text-4xl">
                Ready to help your street?
              </h2>
              <p className="mt-3 max-w-xl text-white/75">
                Report a hazard, browse nearby issues, and keep the conversation
                going until the road gets attention.
              </p>
              <div className="mt-7 flex flex-col gap-3 sm:flex-row">
                <Link
                  to="/report"
                  className="rounded-xl bg-amber-500 px-6 py-3 text-center font-semibold text-navy-950 transition hover:bg-amber-400"
                >
                  Report a pothole
                </Link>
                <Link
                  to="/potholes"
                  className="rounded-xl border border-white/20 bg-white/5 px-6 py-3 text-center font-semibold text-white transition hover:bg-white/10"
                >
                  Browse reports
                </Link>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {[
                ["Live reports", "Community-sourced"],
                ["Photo proof", "Clear evidence"],
                ["Votes", "Priority signal"],
                ["Status", "From open to fixed"],
              ].map(([title, subtitle]) => (
                <div
                  key={title}
                  className="rounded-2xl border border-white/10 bg-white/5 p-4 backdrop-blur"
                >
                  <p className="font-display text-lg font-semibold">{title}</p>
                  <p className="text-sm text-white/65">{subtitle}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
